<template>
  <div
    class="tw-rounded-sm tw-bg-white tw-pt-4"
    :data-test-id="`${tier}-emails-chart`"
  >
    <card-placeholder-banner
      :data-test-id="`${tier}-emails-chart-banner`"
      type="EMAIL_SECURITY"
    >
      <template #title>{{ $t("emails-analyzed-vs-incidents") }}</template>
      <template #text>
        {{ $t("dashboard-demo-email-security") }}
      </template>
    </card-placeholder-banner>
    <div class="tw-relative tw-h-96 tw-w-full tw-p-4">
      <canvas
        id="emails-chart-placeholder"
        :data-test-id="`${tier}-emails-chart-canvas`"
        width="100"
        height="auto"
        role="image"
      ></canvas>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import CardPlaceholderBanner from "../CardPlaceholderBanner/CardPlaceholderBanner.vue";

import Chart from "chart.js/auto";

import { useIsMspRoute } from "@/composables/useIsMspRoute";

const { isMspRoute } = useIsMspRoute();

const tier = isMspRoute.value ? "msp" : "customer";

const style = getComputedStyle(document.body);
const textColor = computed(() => style.getPropertyValue("--chartWhite"));

const blue = "rgb(14, 156, 255)";
const red = "rgb(210, 63, 63)";

const { t } = useI18n();

const emailsAnalyzedMock = [
  123, 160, 187, 145, 249, 160, 176, 234, 198, 112, 92, 155, 138, 217, 164, 97,
  250, 78, 191, 129, 242, 78, 161, 105, 237, 156, 179, 148, 210, 84,
];

const incidentsMock = [
  12, 2, 18, 14, 24, 100, 17, 23, 19, 11, 2, 15, 13, 21, 16, 9, 100, 1, 19, 12,
  22, 7, 11, 15, 23, 5, 17, 18, 20, 8,
];

const labels = [
  "2024-09-01",
  "2024-09-02",
  "2024-09-03",
  "2024-09-04",
  "2024-09-05",
  "2024-09-06",
  "2024-09-07",
  "2024-09-08",
  "2024-09-09",
  "2024-09-10",
  "2024-09-11",
  "2024-09-12",
  "2024-09-13",
  "2024-09-14",
  "2024-09-15",
  "2024-09-16",
  "2024-09-17",
  "2024-09-18",
  "2024-09-19",
  "2024-09-20",
  "2024-09-21",
  "2024-09-22",
  "2024-09-23",
  "2024-09-24",
  "2024-09-25",
  "2024-09-26",
  "2024-09-27",
  "2024-09-28",
  "2024-09-29",
  "2024-09-30",
].map((item: string) => {
  const date = new Date(item);
  const formatter = new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return formatter.format(date);
});

const initChart = () => {
  const htmlElement = document.getElementById("emails-chart-placeholder");

  if (!(htmlElement instanceof HTMLCanvasElement)) {
    return;
  }

  const ctx = htmlElement.getContext("2d");

  if (!ctx) {
    return;
  }

  new Chart(ctx, {
    type: "line",
    data: {
      labels,
      datasets: [
        {
          label: t("emails-analyzed-legend"),
          data: emailsAnalyzedMock,
          pointBackgroundColor: blue,
          borderColor: blue,
          borderWidth: 2,
          pointRadius: 3,
          tension: 0.4,
          fill: false,
          type: "line",
          yAxisID: "y",
        },
        {
          label: t("incidents-legend"),
          data: incidentsMock,
          pointBackgroundColor: red,
          borderColor: red,
          borderWidth: 2,
          pointRadius: 3,
          tension: 0.4,
          fill: false,
          type: "line",
          yAxisID: "y1",
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "index",
        intersect: false,
      },
      scales: {
        x: {
          grid: {
            display: false,
            drawTicks: false,
          },
          border: {
            color: "#ddd",
            display: true,
          },
          ticks: {
            padding: 12,
            color: textColor.value,
          },
          offset: false,
        },
        y: {
          beginAtZero: true,
          position: "left",
          title: {
            display: true,
            text: t("emails-analyzed-legend"),
            color: blue,
            font: {
              size: 18,
            },
          },
          border: {
            display: false,
          },
          grid: {
            display: false,
            drawTicks: false,
          },
          ticks: {
            padding: 12,
            precision: 0,
            color: textColor.value,
          },
        },
        y1: {
          beginAtZero: true,
          position: "right",
          title: {
            display: true,
            text: t("incidents-legend"),
            color: red,
            font: {
              size: 18,
            },
          },
          border: {
            display: false,
          },
          grid: {
            display: false,
            drawTicks: false,
          },
          ticks: {
            padding: 12,
            precision: 0,
            color: textColor.value,
          },
        },
      },
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            usePointStyle: true,
            padding: 24,
            color: textColor.value,
          },
        },
      },
    },
  });
};

onMounted(() => initChart());
</script>
